import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  useTheme,
  alpha,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Person,
  Security,
  Notifications,
  Language,
  Palette,
  Delete,
  Edit,
  Visibility,
  VisibilityOff,
  Public,
  Lock,
  Badge,
  EmojiEvents,
  Save,
  Cancel,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import EditProfile from './EditProfile';
import BadgeSettings from './BadgeSettings';
import { normalizeLanguagePreferences } from '../../utils/languageUtils';

const UserSettings = () => {
  const { user, updateUserProfile, signOut } = useAuth();
  const { addNotification, themeMode, toggleTheme } = useApp();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [settings, setSettings] = useState({
    watchlistPublic: true,
    showBadges: true,
    allowFollowers: true,
    showActivity: true,
    emailNotifications: true,
    pushNotifications: true,
  });

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [badgeSettingsOpen, setBadgeSettingsOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const primaryColor = isDark ? '#ffd700' : '#1976d2';
  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #ffd700 0%, #ffb300 100%)'
    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';

  const cardStyle = {
    background: isDark 
      ? 'rgba(30, 30, 30, 0.95)' 
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(primaryColor, 0.2)}`,
    borderRadius: 3,
    boxShadow: isDark
      ? '0 8px 32px rgba(0, 0, 0, 0.3)'
      : '0 8px 32px rgba(0, 0, 0, 0.1)',
  };

  useEffect(() => {
    if (user) {
      // Load badge settings from user data
      let showBadges = true;
      try {
        const badgeSettings = user.badgeDisplaySettings ? 
          JSON.parse(user.badgeDisplaySettings) : {};
        showBadges = badgeSettings.showBadges !== false;
      } catch (error) {
        console.error('Error parsing badge settings:', error);
      }
      
      // Load other settings from localStorage
      const allowFollowers = localStorage.getItem('setting_allowFollowers') !== 'false';
      const showActivity = localStorage.getItem('setting_showActivity') !== 'false';
      const emailNotifications = localStorage.getItem('setting_emailNotifications') !== 'false';
      const pushNotifications = localStorage.getItem('setting_pushNotifications') !== 'false';
      
      setSettings({
        watchlistPublic: user.watchlistPublic !== false,
        showBadges,
        allowFollowers,
        showActivity,
        emailNotifications,
        pushNotifications,
      });
    }
  }, [user]);

  const handleSettingChange = async (setting, value) => {
    setLoading(true);
    try {
      const newSettings = { ...settings, [setting]: value };
      setSettings(newSettings);

      // Handle different settings with backend updates
      const updateData = {};
      
      switch (setting) {
        case 'watchlistPublic':
          updateData.watchlistPublic = value;
          break;
        case 'showBadges':
          // Update badge display settings
          const currentSettings = user.badgeDisplaySettings ? 
            JSON.parse(user.badgeDisplaySettings) : {};
          updateData.badgeDisplaySettings = JSON.stringify({
            ...currentSettings,
            showBadges: value
          });
          break;
        case 'allowFollowers':
        case 'showActivity':
        case 'emailNotifications':
        case 'pushNotifications':
          // These would need backend schema updates
          // For now, store in localStorage as user preferences
          localStorage.setItem(`setting_${setting}`, value.toString());
          break;
      }
      
      if (Object.keys(updateData).length > 0) {
        await updateUserProfile(updateData);
      }

      // Show different message for WIP features
      const isWipFeature = ['allowFollowers', 'showActivity', 'emailNotifications', 'pushNotifications'].includes(setting);
      
      addNotification({
        type: isWipFeature ? 'info' : 'success',
        message: isWipFeature 
          ? 'Setting saved locally. Full backend integration coming soon!' 
          : 'Setting updated successfully',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to update setting',
      });
      setSettings(prev => ({ ...prev, [setting]: !value }));
    } finally {
      setLoading(false);
    }
  };

  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      addNotification({
        type: 'error',
        message: 'Please type DELETE to confirm account deletion',
      });
      return;
    }
    
    setLoading(true);
    try {
      // Sign out user first
      await signOut();
      
      // Show success message
      addNotification({
        type: 'info',
        message: 'Account deletion request submitted. You have been signed out.',
      });
      
      setDeleteAccountOpen(false);
      
      // Note: Actual deletion would need backend Lambda function
      // This is a safe implementation that signs out the user
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to process account deletion request',
      });
    } finally {
      setLoading(false);
    }
  };

  const settingSections = [
    {
      title: 'Profile & Privacy',
      icon: <Person sx={{ color: primaryColor }} />,
      items: [
        {
          title: 'Edit Profile',
          subtitle: 'Update your profile information, avatar, and bio',
          action: 'button',
          buttonText: 'Edit',
          onClick: () => setEditProfileOpen(true),
        },
        {
          title: 'Public Watched List',
          subtitle: 'Allow others to see your watched movies list',
          action: 'switch',
          value: settings.watchlistPublic,
          onChange: (value) => handleSettingChange('watchlistPublic', value),
        },
        {
          title: 'Show Activity',
          subtitle: 'Display your recent activity to followers (WIP)',
          action: 'switch',
          value: settings.showActivity,
          onChange: (value) => handleSettingChange('showActivity', value),
          wip: true,
        },
        {
          title: 'Allow Followers',
          subtitle: 'Let other users follow your profile (In Development)',
          action: 'switch',
          value: settings.allowFollowers,
          onChange: (value) => handleSettingChange('allowFollowers', value),
          wip: true,
        },
      ],
    },
    {
      title: 'Badges & Achievements',
      icon: <EmojiEvents sx={{ color: primaryColor }} />,
      items: [
        {
          title: 'Badge & Title Settings',
          subtitle: 'Customize badges, achievements, and titles display',
          action: 'button',
          buttonText: 'Manage',
          onClick: () => setBadgeSettingsOpen(true),
        },
        {
          title: 'Show Badges',
          subtitle: 'Display badges on your profile and posts',
          action: 'switch',
          value: settings.showBadges,
          onChange: (value) => handleSettingChange('showBadges', value),
        },
      ],
    },
    {
      title: 'Notifications',
      icon: <Notifications sx={{ color: primaryColor }} />,
      items: [
        {
          title: 'Email Notifications',
          subtitle: 'Receive updates via email (Coming Soon)',
          action: 'switch',
          value: settings.emailNotifications,
          onChange: (value) => handleSettingChange('emailNotifications', value),
          wip: true,
        },
        {
          title: 'Push Notifications',
          subtitle: 'Get real-time notifications in browser (In Development)',
          action: 'switch',
          value: settings.pushNotifications,
          onChange: (value) => handleSettingChange('pushNotifications', value),
          wip: true,
        },
      ],
    },
    {
      title: 'Appearance',
      icon: <Palette sx={{ color: primaryColor }} />,
      items: [
        {
          title: 'Theme',
          subtitle: `Currently using ${isDark ? 'dark' : 'light'} theme`,
          action: 'button',
          buttonText: isDark ? 'Light Mode' : 'Dark Mode',
          onClick: toggleTheme,
        },
      ],
    },
    {
      title: 'Account',
      icon: <Security sx={{ color: primaryColor }} />,
      items: [
        {
          title: 'Sign Out',
          subtitle: 'Sign out of your account',
          action: 'button',
          buttonText: 'Sign Out',
          onClick: signOut,
          color: 'error',
        },
        {
          title: 'Delete Account',
          subtitle: 'Permanently delete your account and data (Beta)',
          action: 'button',
          buttonText: 'Delete',
          onClick: () => setDeleteAccountOpen(true),
          color: 'error',
          wip: true,
        },
      ],
    },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ 
          background: primaryGradient,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account preferences and privacy settings
        </Typography>
      </Box>

      <Stack spacing={3}>
        {settingSections.map((section, index) => (
          <Card key={index} sx={cardStyle}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {section.icon}
                <Typography variant="h6" fontWeight={600}>
                  {section.title}
                </Typography>
              </Box>

              <List disablePadding>
                {section.items.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" fontWeight={500}>
                              {item.title}
                            </Typography>
                            {item.wip && (
                              <Chip 
                                label="WIP" 
                                size="small" 
                                sx={{ 
                                  height: 18,
                                  fontSize: '0.7rem',
                                  bgcolor: 'warning.main',
                                  color: 'white',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={item.subtitle}
                      />
                      <ListItemSecondaryAction>
                        {item.action === 'switch' ? (
                          <Switch
                            checked={item.value}
                            onChange={(e) => item.onChange(e.target.checked)}
                            disabled={loading}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: primaryColor,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: primaryColor,
                              },
                            }}
                          />
                        ) : (
                          <Button
                            variant={item.color === 'error' ? 'outlined' : 'contained'}
                            color={item.color || 'primary'}
                            size="small"
                            onClick={item.onClick}
                            disabled={loading}
                            sx={{
                              borderRadius: 2,
                              ...(item.color !== 'error' && {
                                background: primaryGradient,
                                '&:hover': {
                                  background: isDark
                                    ? 'linear-gradient(135deg, #e6c200 0%, #f0d000 100%)'
                                    : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                },
                              }),
                            }}
                          >
                            {item.buttonText}
                          </Button>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                    {itemIndex < section.items.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        ))}

        {/* User Info Card */}
        <Card sx={cardStyle}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Person sx={{ color: primaryColor }} />
              <Typography variant="h6" fontWeight={600}>
                Account Information
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {user?.titles && user.titles.length > 0 && (
                <Chip 
                  label={user.titles[0]} 
                  size="small" 
                  sx={{ 
                    background: primaryGradient,
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              )}
              <Chip 
                label={user?.email} 
                size="small" 
                variant="outlined"
                sx={{ borderColor: primaryColor, color: primaryColor }}
              />
              {user?.languagePreferences?.length > 0 && (
                <Chip 
                  label={`${user.languagePreferences.length} languages`} 
                  size="small" 
                  variant="outlined"
                  sx={{ borderColor: primaryColor, color: primaryColor }}
                />
              )}
              {user?.titles && user.titles.length > 1 && (
                <Chip 
                  label={`+${user.titles.length - 1} more titles`} 
                  size="small" 
                  variant="outlined"
                  sx={{ borderColor: primaryColor, color: primaryColor }}
                />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              Member since {new Date(user?.createdAt).toLocaleDateString()}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Edit Profile Dialog */}
      <EditProfile
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        user={user}
        onUpdate={(updatedUser) => {
          // Handle user update
          setEditProfileOpen(false);
        }}
      />

      {/* Badge Settings Dialog */}
      <BadgeSettings
        open={badgeSettingsOpen}
        onClose={() => setBadgeSettingsOpen(false)}
        user={user}
        onUpdate={(updatedUser) => {
          // Handle user update
          setBadgeSettingsOpen(false);
        }}
      />

      {/* Delete Account Confirmation */}
      <Dialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        PaperProps={{
          sx: {
            ...cardStyle,
            maxWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'error.main'
        }}>
          <Warning />
          Delete Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This will remove:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, mb: 3 }}>
            <Typography component="li" variant="body2">Your profile and posts</Typography>
            <Typography component="li" variant="body2">Your watchlists and reviews</Typography>
            <Typography component="li" variant="body2">Your followers and following</Typography>
            <Typography component="li" variant="body2">All achievements and badges</Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Type "DELETE" to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE here"
            error={deleteConfirmText && deleteConfirmText !== 'DELETE'}
            helperText={deleteConfirmText && deleteConfirmText !== 'DELETE' ? 'Must type DELETE exactly' : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteAccountOpen(false);
              setDeleteConfirmText('');
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={loading || deleteConfirmText !== 'DELETE'}
          >
            {loading ? 'Processing...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserSettings;